import { useMemo, useState } from "react";
import {
  AD_LEVELS, AD_LEVEL_LABELS,
  api, type AdLevel, type AdPerformanceRow, type AttributionChannel, type AttributionReport,
  type AttributionSale, type ProductRow,
} from "../api";
import { useAsync, type Async } from "../useAsync";
import { channelColor, channelLabel, count, money, shortTime, DASH } from "../format";

const RANGES = [7, 30, 90];
const TABS = [
  { id: "channels" as const, label: "Channels" },
  { id: "ads" as const, label: "Ads" },
  { id: "sales" as const, label: "Sales" },
];

// A sale can miss an ad for two very different reasons, and collapsing them
// would read as broken tracking. 'unattributed' = no paid click at all;
// 'no_dimension' = there WAS an ad, it just has nothing at this grain.
const NO_DIMENSION_LABEL: Record<AdLevel, string> = {
  campaign: "Ads with no campaign id",
  ad_group: "Ads with no ad group",
  keyword: "Ads with no keyword",
  ad: "Ads with no creative id",
};
const KIND_LABELS: Record<AttributionSale["kind"], string> = {
  charge: "Charge",
  usage: "Usage",
  invoice: "Invoice",
  refund: "Refund",
  dispute: "Dispute",
  adjustment: "Adjustment",
};

/**
 * Attribution answers: where does revenue actually come from, and what did it
 * cost? Two models side by side (first touch = what starts journeys, last
 * touch = what closes them), a per-channel P&L, a coverage panel, and a sales
 * ledger that lists every transaction in the window with its first and last
 * touch. Unattributed is always its own row — never smeared.
 */
export function Attribution({ product }: { product: ProductRow }) {
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("channels");
  const report = useAsync(() => api.attribution(product.slug, days), [product.slug, days]);

  return (
    <div className="page" style={{ paddingTop: 26 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h1>Attribution</h1>
          <span className="sub">
            {tab === "sales"
              ? "Every sale in the window, with the channel that started the journey and the one that closed it."
              : tab === "ads"
              ? "Below the channel line: what each campaign, ad group, keyword and creative actually returned."
              : "Where revenue comes from and what it cost — first touch vs last touch, honestly."}
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6 }}>
          {RANGES.map((d) => (
            <button key={d} type="button" className="seg" aria-pressed={days === d} onClick={() => setDays(d)}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, padding: "18px 0 0" }}>
        {TABS.map((t) => (
          <button key={t.id} type="button" className="seg" aria-pressed={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "channels" && <ChannelsTab report={report} days={days} currency={product.currency} />}
      {tab === "ads" && <AdsTab slug={product.slug} days={days} currency={product.currency} />}
      {tab === "sales" && <SalesTab key={`${product.slug}:${days}`} slug={product.slug} days={days} />}
    </div>
  );
}

function ChannelsTab({
  report, days, currency,
}: {
  report: Async<AttributionReport>;
  days: number;
  currency: string;
}) {
  if (report.error) return <PageProblem message={report.error} />;
  if (report.loading || !report.data) return <PageLoading />;
  const r = report.data;
  const cur = currency;

  const attributedShare = r.totals.revenue_cents > 0
    ? (r.totals.revenue_cents - r.totals.unattributed_cents) / r.totals.revenue_cents
    : null;
  const blendedRoas = r.totals.spend_cents > 0 ? r.totals.attributed_first_cents / r.totals.spend_cents : null;
  const showConversions = r.totals.revenue_cents === 0 && (r.totals.conversions_first ?? 0) > 0;
  const goalLabel = r.primary_goal?.label ?? "Primary conversions";

  const ranked = [...r.channels].sort((a, b) => {
    if (showConversions) {
      return (
        Math.max(b.conversions_first ?? 0, b.conversions_last ?? 0, b.spend_cents) -
        Math.max(a.conversions_first ?? 0, a.conversions_last ?? 0, a.spend_cents)
      );
    }
    return (
      Math.max(b.revenue_first_cents, b.revenue_last_cents, b.spend_cents) -
      Math.max(a.revenue_first_cents, a.revenue_last_cents, a.spend_cents)
    );
  });
  const maxMetric = showConversions
    ? Math.max(...r.channels.map((c) => Math.max(c.conversions_first ?? 0, c.conversions_last ?? 0)), 1)
    : Math.max(...r.channels.map((c) => Math.max(c.revenue_first_cents, c.revenue_last_cents)), 1);

  return (
    <>
      <div className="metrics" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", padding: "26px 0 24px" }}>
        {showConversions ? (
          <>
            <Stat label={`${goalLabel} (${days}d)`} value={count(r.totals.conversions_first ?? 0)} />
            <Stat
              label="Attributed"
              value={attributedShare === null ? (r.totals.conversions_first ? "100%" : DASH) : `${Math.round(attributedShare * 100)}%`}
              tone="var(--pos)"
              note={r.primary_goal?.score_mode === "grade" ? "First-touch attribution" : "First touch mapped"}
            />
            <Stat label={`Spend ${days}d`} value={money(r.totals.spend_cents, cur)} />
            <Stat
              label="Blended CPA"
              value={r.totals.blended_cpa_cents !== null && r.totals.blended_cpa_cents !== undefined ? money(r.totals.blended_cpa_cents, cur) : DASH}
              tone={
                r.totals.blended_cpa_cents != null && r.primary_goal?.target_cpa_cents != null
                  ? r.totals.blended_cpa_cents <= r.primary_goal.target_cpa_cents
                    ? "var(--pos)"
                    : "var(--warn)"
                  : undefined
              }
              note={
                r.primary_goal?.target_cpa_cents != null
                  ? `Target: ${money(r.primary_goal.target_cpa_cents, cur)}`
                  : "spend / conversions"
              }
            />
          </>
        ) : (
          <>
            <Stat label={`Revenue ${days}d`} value={money(r.totals.revenue_cents, cur)} />
            <Stat
              label="Attributed"
              value={attributedShare === null ? DASH : `${Math.round(attributedShare * 100)}%`}
              tone={attributedShare === null ? undefined : attributedShare < 0.6 ? "var(--warn)" : "var(--pos)"}
              note={attributedShare === null ? "no revenue in window" : `${money(r.totals.unattributed_cents, cur)} unattributed`}
            />
            <Stat label={`Spend ${days}d`} value={money(r.totals.spend_cents, cur)} />
            <Stat
              label="Blended ROAS"
              value={blendedRoas === null ? DASH : `${blendedRoas.toFixed(2)}×`}
              tone={blendedRoas === null ? undefined : blendedRoas >= 1 ? "var(--pos)" : "var(--neg)"}
              note={blendedRoas === null ? "no spend in window" : "attributed revenue / spend"}
            />
          </>
        )}
      </div>

      {/* ------------------------------------------- first vs last touch */}
      <section className="panel" style={{ marginTop: 12 }}>
        <div className="panel-head">
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <h2>First touch vs last touch</h2>
            <span className="sub">
              Solid = first touch (what starts journeys). Hollow = last touch (what closes them).
              A big gap means a channel opens or closes far more than it gets credit for.
            </span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 13, marginTop: 20 }}>
          {ranked
            .filter((c) =>
              showConversions
                ? (c.conversions_first ?? 0) !== 0 || (c.conversions_last ?? 0) !== 0
                : c.revenue_first_cents !== 0 || c.revenue_last_cents !== 0
            )
            .map((c) => (
              <ModelBars
                key={c.channel}
                c={c}
                max={maxMetric}
                currency={cur}
                useConversions={showConversions}
              />
            ))}
          {ranked.every((c) =>
            showConversions
              ? (c.conversions_first ?? 0) === 0 && (c.conversions_last ?? 0) === 0
              : c.revenue_first_cents === 0 && c.revenue_last_cents === 0
          ) && (
            <div style={{ padding: "24px 0", textAlign: "center", color: "var(--text-faint)", fontSize: 13 }}>
              {showConversions ? "No conversions in this window yet." : "No revenue in this window yet."}
            </div>
          )}
        </div>
      </section>

      {/* --------------------------------------------------- channel P&L */}
      <section className="panel" style={{ marginTop: 22 }}>
        <div className="panel-head">
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <h2>{showConversions ? "Channel Outcomes & CPA" : "Channel P&L"}</h2>
            <span className="sub">
              {showConversions
                ? `Spend against first-touch ${goalLabel.toLowerCase()} and lead quality, last ${days} days.`
                : `Spend against first-touch outcomes, per channel, last ${days} days.`}
            </span>
          </div>
        </div>
        <div style={{ overflowX: "auto", marginTop: 8 }}>
          <table className="ptable">
            <thead>
              <tr>
                <th>Channel</th>
                <th>Spend</th>
                {showConversions ? (
                  <>
                    <th>{goalLabel} (1st)</th>
                    <th>{goalLabel} (last)</th>
                    <th>CPA (1st)</th>
                    {r.primary_goal?.score_mode === "grade" && <th>Grade A</th>}
                    {r.primary_goal?.score_mode === "numeric" && <th>Avg Score</th>}
                  </>
                ) : (
                  <>
                    <th>Revenue (1st)</th>
                    <th>Revenue (last)</th>
                    <th>ROAS</th>
                    <th>Customers</th>
                    <th>CAC</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {ranked.map((c) => (
                <tr key={c.channel} style={{ cursor: "default" }}>
                  <td><div className="cell" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", flex: "none",
                                   background: c.channel === "unattributed" ? "var(--muted)" : channelColor(c.channel) }} />
                    <span style={{ color: c.channel === "unattributed" ? "var(--muted)" : undefined }}>
                      {channelLabel(c.channel)}
                    </span>
                  </div></td>
                  <td><div className="cell num">{c.spend_cents ? money(c.spend_cents, cur) : DASH}</div></td>
                  {showConversions ? (
                    <>
                      <td><div className="cell num">{count(c.conversions_first ?? 0)}</div></td>
                      <td><div className="cell num">{count(c.conversions_last ?? 0)}</div></td>
                      <td><div className="cell num">{c.cpa_first_cents !== null && c.cpa_first_cents !== undefined ? money(c.cpa_first_cents, cur) : DASH}</div></td>
                      {r.primary_goal?.score_mode === "grade" && (
                        <td><div className="cell num" style={{ color: (c.grade_a_first ?? 0) > 0 ? "var(--pos)" : undefined }}>
                          {count(c.grade_a_first ?? 0)}
                        </div></td>
                      )}
                      {r.primary_goal?.score_mode === "numeric" && (
                        <td><div className="cell num">
                          {c.avg_score_first !== null && c.avg_score_first !== undefined ? c.avg_score_first : DASH}
                        </div></td>
                      )}
                    </>
                  ) : (
                    <>
                      <td><div className="cell num">{money(c.revenue_first_cents, cur)}</div></td>
                      <td><div className="cell num">{money(c.revenue_last_cents, cur)}</div></td>
                      <td><div className="cell num" style={{ color: roasTone(c.roas) }}>
                        {c.roas === null ? DASH : `${Number(c.roas).toFixed(2)}×`}
                      </div></td>
                      <td><div className="cell num">{count(c.customers_first)}</div></td>
                      <td><div className="cell num">{c.cac_cents === null ? DASH : money(c.cac_cents, cur)}</div></td>
                    </>
                  )}
                </tr>
              ))}
              {ranked.length === 0 && (
                <tr><td colSpan={showConversions ? 6 : 7}><div className="cell" style={{ color: "var(--text-4)" }}>
                  Nothing to attribute yet — no spend or conversions in this window.
                </div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ------------------------------------------------------ coverage */}
      <section className="panel" style={{ marginTop: 22 }}>
        <div className="panel-head">
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <h2>How trustworthy is this?</h2>
            <span className="sub">Attribution is only as good as its inputs. These are the inputs.</span>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 18, marginTop: 18 }}>
          <Coverage label="Identities with a first touch"
                    n={r.coverage.identities_with_channel} d={r.coverage.identities_total}
                    fix="Capture UTMs/click ids at first visit so journeys start attributed." />
          <Coverage label="Events carrying a channel"
                    n={r.coverage.events_with_channel} d={r.coverage.events_total}
                    fix="Add UTMs to everything you ship; check adapter channel mapping." />
          <Coverage label="Transactions tied to an identity"
                    n={r.coverage.transactions_with_identity} d={r.coverage.transactions_total}
                    fix="Map email / external id columns in the revenue source's event_maps." />
        </div>
      </section>

      <div className="banner" style={{ marginTop: 22 }}>
        <span>
          Revenue with no identifiable first touch is the <strong>Unattributed</strong> row above.
          It is never divided across channels — a smeared number would be a lie with good posture.
        </span>
      </div>
    </>
  );
}

/**
 * ROI one level down from channel. Cost comes from the platform at the chosen
 * grain; revenue comes from the ad ids captured on the identity's first click.
 * The two do not fully reconcile and are not made to: spend on campaign types
 * with nothing at this grain gets its own line, as does revenue with no ad.
 */
function AdsTab({ slug, days, currency }: { slug: string; days: number; currency: string }) {
  const [level, setLevel] = useState<AdLevel>("campaign");
  const report = useAsync(() => api.attributionAds(slug, days, level), [slug, days, level]);

  const levelPicker = (
    <div style={{ display: "flex", gap: 6, padding: "18px 0 0", flexWrap: "wrap" }}>
      {AD_LEVELS.map((l) => (
        <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
          {AD_LEVEL_LABELS[l]}
        </button>
      ))}
    </div>
  );

  if (report.error) return <>{levelPicker}<PageProblem message={report.error} /></>;
  if (report.loading || !report.data) return <>{levelPicker}<PageLoading /></>;
  const r = report.data;
  const named = r.rows.filter((x) => x.key !== "unattributed" && x.key !== "no_dimension");
  const roas = r.totals.detail_spend_cents > 0
    ? r.totals.attributed_first_cents / r.totals.detail_spend_cents
    : null;
  const placed = r.coverage.paid_identities > 0
    ? r.coverage.identities_with_dimension / r.coverage.paid_identities
    : null;

  return (
    <>
      {levelPicker}

      <div className="metrics" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", padding: "26px 0 24px" }}>
        <Stat
          label={`Spend by ${AD_LEVEL_LABELS[level].toLowerCase()}`}
          value={money(r.totals.detail_spend_cents, currency)}
          note={r.totals.spend_without_detail_cents > 0
            ? `${money(r.totals.spend_without_detail_cents, currency)} has none`
            : `of ${money(r.totals.spend_cents, currency)} total`}
        />
        <Stat
          label="Revenue placed"
          value={money(r.totals.attributed_first_cents, currency)}
          note={`${money(r.totals.no_dimension_cents + r.totals.unattributed_cents, currency)} could not be`}
        />
        <Stat
          label="ROAS at this grain"
          value={roas === null ? DASH : `${roas.toFixed(2)}×`}
          tone={roas === null ? undefined : roas >= 1 ? "var(--pos)" : "var(--neg)"}
          note={roas === null ? "no spend at this grain" : "placed revenue / spend"}
        />
        <Stat
          label="Clicks identified"
          value={placed === null ? DASH : `${Math.round(placed * 100)}%`}
          tone={placed === null ? undefined : placed >= 0.8 ? "var(--pos)" : placed >= 0.4 ? "var(--warn)" : "var(--neg)"}
          note={placed === null ? "no paid identities yet" : `${count(r.coverage.identities_with_dimension)} of ${count(r.coverage.paid_identities)} paid`}
        />
      </div>

      <section className="panel" style={{ marginTop: 12 }}>
        <div className="panel-head">
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <h2>{AD_LEVEL_LABELS[level]} P&amp;L</h2>
            <span className="sub">
              Spend against first-touch outcomes per {AD_LEVEL_LABELS[level].toLowerCase()}, last {days} days.
            </span>
          </div>
        </div>
        <div style={{ overflowX: "auto", marginTop: 8 }}>
          <table className="ptable">
            <thead>
              <tr>
                <th>{AD_LEVEL_LABELS[level]}</th>
                <th>Spend</th>
                <th>Clicks</th>
                <th>Revenue (1st)</th>
                <th>Revenue (last)</th>
                <th>ROAS</th>
                <th>Customers</th>
                <th>CAC</th>
              </tr>
            </thead>
            <tbody>
              {r.rows.map((row) => (
                <AdRow key={row.key} row={row} level={level} currency={currency} />
              ))}
              {r.rows.length === 0 && (
                <tr><td colSpan={8}><div className="cell" style={{ color: "var(--text-4)" }}>
                  Nothing at this grain yet — connect Google Ads and set the tracking parameters in Integrations.
                </div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {named.length > 0 && r.totals.spend_without_detail_cents > 0 && (
        <div className="banner" style={{ marginTop: 22 }}>
          <span>
            {money(r.totals.spend_without_detail_cents, currency)} of campaign spend has no{" "}
            {AD_LEVEL_LABELS[level].toLowerCase()} to attach it to — Performance Max, Shopping and
            Demand Gen don't report at this grain. It is shown as its own line, never divided across
            the rows above.
          </span>
        </div>
      )}

      {placed !== null && placed < 0.8 && (
        <div className="banner" style={{ marginTop: 14 }}>
          <span>
            Only {Math.round(placed * 100)}% of paid visitors carry a {AD_LEVEL_LABELS[level].toLowerCase()}.
            Add FunnelKeeper's tracking parameters to the Google Ads account (Integrations → Google Ads)
            so every future click names the ad that produced it.
          </span>
        </div>
      )}
    </>
  );
}

function AdRow({ row, level, currency }: { row: AdPerformanceRow; level: AdLevel; currency: string }) {
  const sentinel = row.key === "unattributed" || row.key === "no_dimension";
  const name = row.key === "unattributed"
    ? "Unattributed"
    : row.key === "no_dimension"
    ? NO_DIMENSION_LABEL[level]
    : row.label || `#${row.key}`;
  // Context under the name: which campaign/ad group this sits in, and the match
  // type — the thing that decides whether a keyword's ROAS is even comparable.
  const context = sentinel
    ? row.key === "unattributed" ? "no paid click on record" : "the ad has none at this grain"
    : [level !== "campaign" ? row.campaign_name : null,
       level === "ad" || level === "keyword" ? row.ad_group_name : null,
       row.match_type].filter(Boolean).join(" · ");

  return (
    <tr style={{ cursor: "default" }}>
      <td>
        <div className="cell" style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
          <span style={{ color: sentinel ? "var(--muted)" : undefined }}>{name}</span>
          {context && <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>{context}</span>}
        </div>
      </td>
      <td><div className="cell num">{row.spend_cents ? money(row.spend_cents, row.currency || currency) : DASH}</div></td>
      <td><div className="cell num">{row.clicks ? count(row.clicks) : DASH}</div></td>
      <td><div className="cell num">{money(row.revenue_first_cents, currency)}</div></td>
      <td><div className="cell num">{money(row.revenue_last_cents, currency)}</div></td>
      <td><div className="cell num" style={{ color: roasTone(row.roas) }}>
        {row.roas === null ? DASH : `${Number(row.roas).toFixed(2)}×`}
      </div></td>
      <td><div className="cell num">{count(row.customers_first)}</div></td>
      <td><div className="cell num">{row.cac_cents === null ? DASH : money(row.cac_cents, currency)}</div></td>
    </tr>
  );
}

function SalesTab({ slug, days }: { slug: string; days: number }) {
  const [channel, setChannel] = useState<string>("all");
  const data = useAsync(() => api.attributionSales(slug, days, { limit: 1000 }), [slug, days]);

  // Reset the chip when the window (or product) changes so a channel that
  // doesn't exist in the new set can't leave the table empty by accident.
  const rows = data.data?.sales ?? [];
  const channels = useMemo(() => {
    const tally = new Map<string, number>();
    for (const s of rows) tally.set(s.first_channel, (tally.get(s.first_channel) ?? 0) + 1);
    return [...tally.entries()]
      .sort((a, b) => (a[0] === "unattributed" ? 1 : b[0] === "unattributed" ? -1 : b[1] - a[1]))
      .map(([id, n]) => ({ id, n }));
  }, [rows]);

  const activeChannel = channel === "all" || channels.some((c) => c.id === channel) ? channel : "all";
  const shown = activeChannel === "all" ? rows : rows.filter((s) => s.first_channel === activeChannel);

  if (data.error) return <PageProblem message={data.error} />;
  if (data.loading || !data.data) return <PageLoading />;

  const truncated = data.data.total > rows.length;

  return (
    <section className="panel" style={{ marginTop: 22 }}>
      <div className="panel-head">
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h2>Sales</h2>
          <span className="sub">
            {truncated
              ? `Showing the ${count(rows.length)} most recent of ${count(data.data.total)} transactions in the last ${days} days.`
              : `${count(data.data.total)} transaction${data.data.total === 1 ? "" : "s"} in the last ${days} days.`}
            {" "}Unattributed is labelled, never divided.
          </span>
        </div>
      </div>

      {channels.length > 1 && (
        <div style={{ display: "flex", gap: 6, padding: "16px 0 4px", flexWrap: "wrap" }}>
          <button type="button" className="seg" aria-pressed={activeChannel === "all"} onClick={() => setChannel("all")}>
            All
          </button>
          {channels.map((c) => (
            <button key={c.id} type="button" className="seg" aria-pressed={activeChannel === c.id}
                    onClick={() => setChannel(c.id)}>
              {channelLabel(c.id)}
              <span style={{ color: "var(--text-faint)", marginLeft: 6 }}>{c.n}</span>
            </button>
          ))}
        </div>
      )}

      <div style={{ overflowX: "auto", marginTop: 8 }}>
        <table className="ptable">
          <thead>
            <tr>
              <th>When</th>
              <th>Amount</th>
              <th>Kind</th>
              <th>First touch</th>
              <th>Last touch</th>
              <th>Customer</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <SaleRow key={s.id} s={s} />
            ))}
            {shown.length === 0 && (
              <tr style={{ cursor: "default" }}>
                <td colSpan={6}>
                  <div className="cell" style={{ color: "var(--text-4)" }}>
                    {rows.length === 0
                      ? "No sales in this window yet."
                      : "No sales match that first-touch channel."}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SaleRow({ s }: { s: AttributionSale }) {
  const customer = s.email || s.external_id;
  const campaign = utmCampaign(s.first_utm);
  const split = s.first_channel !== s.last_channel;
  const refund = s.amount_cents < 0;
  return (
    <tr style={{ cursor: "default" }}>
      <td>
        <div className="cell" style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
          <span>{shortTime(s.occurred_at)}</span>
          {s.source_ref && (
            <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>{s.source_ref}</span>
          )}
        </div>
      </td>
      <td>
        <div className="cell num" style={{ color: refund ? "var(--neg)" : undefined }}>
          {money(s.amount_cents, s.currency)}
        </div>
      </td>
      <td><div className="cell" style={{ color: "var(--text-2)" }}>{KIND_LABELS[s.kind] ?? s.kind}</div></td>
      <td><div className="cell"><ChannelDot id={s.first_channel} /></div></td>
      <td>
        <div className="cell" style={{ color: split ? undefined : "var(--text-faint)" }}>
          <ChannelDot id={s.last_channel} muted={!split} />
        </div>
      </td>
      <td>
        <div className="cell" style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
          <span style={{ color: customer ? "var(--text-2)" : "var(--muted)" }}>
            {customer ?? "Unlinked"}
          </span>
          {campaign && (
            <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>{campaign}</span>
          )}
        </div>
      </td>
    </tr>
  );
}

function ChannelDot({ id, muted }: { id: string; muted?: boolean }) {
  const un = id === "unattributed";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8,
                   color: muted || un ? "var(--muted)" : "var(--text-2)" }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", flex: "none",
                     background: un ? "var(--muted)" : channelColor(id),
                     opacity: muted && !un ? 0.55 : 1 }} />
      {channelLabel(id)}
    </span>
  );
}

function utmCampaign(utm: Record<string, unknown> | null): string | null {
  if (!utm) return null;
  const campaign = str(utm.campaign);
  if (campaign) return campaign;
  const source = str(utm.source);
  const medium = str(utm.medium);
  if (source && medium) return `${source} / ${medium}`;
  return source || medium || null;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

function Stat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <div className="eyebrow">{label}</div>
      <div className="num" style={{ fontSize: 25, letterSpacing: "-0.03em", whiteSpace: "nowrap", color: tone ?? "var(--text)" }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-faint)" }}>{note ?? ""}</div>
    </div>
  );
}

function ModelBars({
  c,
  max,
  currency,
  useConversions,
}: {
  c: AttributionChannel;
  max: number;
  currency: string;
  useConversions?: boolean;
}) {
  const un = c.channel === "unattributed";
  const color = un ? "var(--muted)" : channelColor(c.channel);
  const firstVal = useConversions ? c.conversions_first ?? 0 : c.revenue_first_cents;
  const lastVal = useConversions ? c.conversions_last ?? 0 : c.revenue_last_cents;
  const w = (v: number) => `${Math.max((v / max) * 100, v > 0 ? 1.5 : 0)}%`;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "150px minmax(0,1fr) 150px", gap: 16, alignItems: "center" }}>
      <span style={{ fontSize: 13, color: un ? "var(--muted)" : "var(--text-2)" }}>{channelLabel(c.channel)}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ height: 11, background: "var(--surface-inset)", borderRadius: 3 }}>
          <div style={{ height: "100%", width: w(firstVal), background: color, borderRadius: 3 }} />
        </div>
        <div style={{ height: 11, borderRadius: 3 }}>
          <div style={{ height: "100%", width: w(lastVal), border: `1.4px solid ${color}`,
                        boxSizing: "border-box", borderRadius: 3, background: "transparent" }} />
        </div>
      </div>
      <div className="num" style={{ fontSize: 12.5, textAlign: "right", color: "var(--text-2)" }}>
        {useConversions ? count(firstVal) : money(firstVal, currency)}
        <span style={{ color: "var(--text-faint)" }}> / {useConversions ? count(lastVal) : money(lastVal, currency)}</span>
      </div>
    </div>
  );
}

function Coverage({ label, n, d, fix }: { label: string; n: number; d: number; fix: string }) {
  const share = d > 0 ? n / d : null;
  const tone = share === null ? "var(--text-faint)" : share >= 0.8 ? "var(--pos)" : share >= 0.5 ? "var(--warn)" : "var(--neg)";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <div className="eyebrow">{label}</div>
      <div className="num" style={{ fontSize: 23, letterSpacing: "-0.03em", color: tone }}>
        {share === null ? DASH : `${Math.round(share * 100)}%`}
        <span style={{ fontSize: 12, color: "var(--text-faint)", marginLeft: 8 }}>{count(n)} of {count(d)}</span>
      </div>
      {share !== null && share < 0.8 && (
        <div style={{ fontSize: 12, color: "var(--text-4)", lineHeight: 1.5 }}>{fix}</div>
      )}
    </div>
  );
}

function roasTone(roas: number | null): string {
  if (roas === null) return "var(--text-faint)";
  return roas >= 1 ? "var(--pos)" : "var(--neg)";
}

function PageLoading() {
  return <div style={{ padding: "60px 0", color: "var(--text-faint)" }}>Loading…</div>;
}
function PageProblem({ message }: { message: string }) {
  return <div style={{ padding: "60px 0", color: "var(--neg)" }}>{message}</div>;
}
